/**
 * Selection utilities for multi-select and rubber band selection
 */

import {
  RackPosition,
  RackDimensions,
  RubberBandState,
  DEFAULT_RUBBER_BAND
} from '../types/warehouseLayout';
import { getRotatedDimensions, screenToWorld } from './positionCalculator';

/**
 * Check if a rack intersects with a rectangular selection area (world coordinates)
 */
export function isRackInSelectionArea(
  rack: RackPosition,
  dimensions: RackDimensions,
  selectionArea: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  }
): boolean {
  const rotated = getRotatedDimensions(dimensions, rack.rotation);

  const rackMinX = rack.x - rotated.width / 2;
  const rackMaxX = rack.x + rotated.width / 2;
  const rackMinY = rack.y - rotated.depth / 2;
  const rackMaxY = rack.y + rotated.depth / 2;

  // Check if rectangles overlap (AABB intersection)
  return !(
    rackMaxX < selectionArea.minX ||
    rackMinX > selectionArea.maxX ||
    rackMaxY < selectionArea.minY ||
    rackMinY > selectionArea.maxY
  );
}

/**
 * Get all racks within a rubber band selection area
 */
export function getRacksInArea(
  racks: RackPosition[],
  rackDimensionsMap: Map<string, RackDimensions>,
  worldX1: number,
  worldY1: number,
  worldX2: number,
  worldY2: number
): string[] {
  const minX = Math.min(worldX1, worldX2);
  const maxX = Math.max(worldX1, worldX2);
  const minY = Math.min(worldY1, worldY2);
  const maxY = Math.max(worldY1, worldY2);

  return racks
    .filter(rack => {
      const dim = rackDimensionsMap.get(rack.rackNumber);
      if (!dim) return false;

      return isRackInSelectionArea(rack, dim, { minX, maxX, minY, maxY });
    })
    .map(rack => rack.rackNumber);
}

/**
 * Calculate bounding box for multiple selected racks (world coordinates)
 */
export function getSelectionBounds(
  selectedRacks: Set<string> | string[],
  racks: RackPosition[],
  rackDimensionsMap: Map<string, RackDimensions>
): { minX: number; maxX: number; minY: number; maxY: number } | null {
  const selectedArray = Array.isArray(selectedRacks) ? selectedRacks : Array.from(selectedRacks);
  if (selectedArray.length === 0) return null;

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  selectedArray.forEach(rackNum => {
    const rack = racks.find(r => r.rackNumber === rackNum);
    const dim = rackDimensionsMap.get(rackNum);

    if (rack && dim) {
      const rotated = getRotatedDimensions(dim, rack.rotation);
      const halfWidth = rotated.width / 2;
      const halfDepth = rotated.depth / 2;

      minX = Math.min(minX, rack.x - halfWidth);
      maxX = Math.max(maxX, rack.x + halfWidth);
      minY = Math.min(minY, rack.y - halfDepth);
      maxY = Math.max(maxY, rack.y + halfDepth);
    }
  });

  if (minX === Infinity) return null;

  return { minX, maxX, minY, maxY };
}

/**
 * Check if user pressed Ctrl/Cmd key (cross-platform multi-select)
 */
export function isMultiSelectKey(event: React.MouseEvent | React.KeyboardEvent | MouseEvent | KeyboardEvent): boolean {
  return event.ctrlKey || event.metaKey; // metaKey = Cmd on Mac
}

/**
 * Check if user pressed Shift key (range selection)
 */
export function isRangeSelectKey(event: React.MouseEvent | React.KeyboardEvent | MouseEvent | KeyboardEvent): boolean {
  return event.shiftKey;
}

/**
 * Convert rubber band screen coordinates to world coordinates selection area
 */
export function rubberBandToWorldArea(
  rubberBand: RubberBandState,
  canvasWidth: number,
  canvasHeight: number,
  warehouseDimensions: { length: number; width: number },
  zoom: number,
  panOffset: { x: number; y: number }
): { minX: number; maxX: number; minY: number; maxY: number } | null {
  if (!rubberBand.isActive) return null;

  const world1 = screenToWorld(
    rubberBand.startX,
    rubberBand.startY,
    canvasWidth,
    canvasHeight,
    { ...warehouseDimensions, height: 0 },
    zoom,
    panOffset
  );

  const world2 = screenToWorld(
    rubberBand.currentX,
    rubberBand.currentY,
    canvasWidth,
    canvasHeight,
    { ...warehouseDimensions, height: 0 },
    zoom,
    panOffset
  );

  return {
    minX: Math.min(world1.x, world2.x),
    maxX: Math.max(world1.x, world2.x),
    minY: Math.min(world1.y, world2.y),
    maxY: Math.max(world1.y, world2.y)
  };
}

/**
 * Create initial rubber band state from mouse position
 */
export function startRubberBand(screenX: number, screenY: number): RubberBandState {
  return {
    isActive: true,
    startX: screenX,
    startY: screenY,
    currentX: screenX,
    currentY: screenY
  };
}

/**
 * Update rubber band current position
 */
export function updateRubberBand(
  current: RubberBandState,
  screenX: number,
  screenY: number
): RubberBandState {
  return {
    ...current,
    currentX: screenX,
    currentY: screenY
  };
}

/**
 * Reset rubber band to inactive state
 */
export function resetRubberBand(): RubberBandState {
  return { ...DEFAULT_RUBBER_BAND };
}

/**
 * Handle selection toggle (Ctrl+click)
 */
export function toggleSelection(
  currentSelection: Set<string>,
  rackNumber: string
): Set<string> {
  const newSelection = new Set(currentSelection);
  if (newSelection.has(rackNumber)) {
    newSelection.delete(rackNumber);
  } else {
    newSelection.add(rackNumber);
  }
  return newSelection;
}

/**
 * Handle single selection (replace all)
 */
export function singleSelect(rackNumber: string): Set<string> {
  return new Set([rackNumber]);
}

/**
 * Handle range selection (Shift+click) - select all racks between indices
 */
export function rangeSelect(
  currentSelection: Set<string>,
  racks: RackPosition[],
  lastClickedRack: string | null,
  clickedRack: string
): Set<string> {
  const newSelection = new Set(currentSelection);

  if (!lastClickedRack) {
    newSelection.add(clickedRack);
    return newSelection;
  }

  const lastIndex = racks.findIndex(r => r.rackNumber === lastClickedRack);
  const currentIndex = racks.findIndex(r => r.rackNumber === clickedRack);

  if (lastIndex === -1 || currentIndex === -1) {
    newSelection.add(clickedRack);
    return newSelection;
  }

  const startIdx = Math.min(lastIndex, currentIndex);
  const endIdx = Math.max(lastIndex, currentIndex);

  for (let i = startIdx; i <= endIdx; i++) {
    newSelection.add(racks[i].rackNumber);
  }

  return newSelection;
}

/**
 * Clear all selection
 */
export function clearSelection(): Set<string> {
  return new Set<string>();
}

/**
 * Select all racks
 */
export function selectAll(racks: RackPosition[]): Set<string> {
  return new Set(racks.map(r => r.rackNumber));
}

/**
 * Calculate center point of selected racks (for group operations)
 */
export function getSelectionCenter(
  selectedRacks: Set<string>,
  racks: RackPosition[]
): { x: number; y: number } | null {
  const selectedArray = Array.from(selectedRacks);
  if (selectedArray.length === 0) return null;

  let sumX = 0;
  let sumY = 0;
  let count = 0;

  selectedArray.forEach(rackNum => {
    const rack = racks.find(r => r.rackNumber === rackNum);
    if (rack) {
      sumX += rack.x;
      sumY += rack.y;
      count++;
    }
  });

  if (count === 0) return null;

  return {
    x: sumX / count,
    y: sumY / count
  };
}
